import { Router } from "@frui.ts/screens";
import { Modules, newNavigationName } from "config";
import CreateAccountingDocumentsInventoryRequest from "entities/createAccountingDocumentsInventoryRequest";
import AccountingDocumentsInventoryRepository from "repositories/accountingDocumentsInventoryRepository";
import EnumService from "services/enumService";
import { action, computed, observable, reaction } from "mobx";
import type { interfaces } from "inversify";
import MonthOptionsService from "services/monthOptionsService";
import NotificationService, { NotificationType } from "services/notificationService";
import { bound } from "@frui.ts/helpers";
import AccountingDocumentsInventoryAccountFilesResponse from "entities/accountingDocumentsInventoryAccountFilesResponse";
import AccountingDocumentsInventoryAccountFilePreviewResponse from "entities/accountingDocumentsInventoryAccountFilePreviewResponse";
import AccountingDocumentsInventoryFileRejectionReason from "entities/accountingDocumentsInventoryFileRejectionReason";
import CreateViewModel from "../../../createViewModel";
import LocalizationService from "../../../../services/localizationService";
import EventBus from "services/eventBus";
import download from "downloadjs";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import AccountingDocumentsInventoryDetailResponse from "entities/accountingDocumentsInventoryDetailResponse";
import AccountingDocumentsInventoryChangeRestrictionLevel from "entities/accountingDocumentsInventoryChangeRestrictionLevel";

const MAX_FILES = 6;
const ALLOWED_FILE_TYPES = [".xlsx", ".xls"];

@Router.registerRoute({ route: newNavigationName, name: Router.Self })
export default class DocumentsInventoryCreateViewModel extends CreateViewModel<CreateAccountingDocumentsInventoryRequest> {
  readonly modelName = "documentsInventory";
  @observable selectedToMonth?: Date;
  @observable userSelectedToMonth = false;
  @observable fileInputRef?: HTMLInputElement;
  @observable createdInventoryId?: string;
  @observable uploadedFilesResponse: AccountingDocumentsInventoryAccountFilePreviewResponse[] = [];
  @observable initialUploadedFiles: AccountingDocumentsInventoryAccountFilePreviewResponse[] = [];
  @observable initialToMonth?: Date;
  @observable detailResponse?: AccountingDocumentsInventoryDetailResponse;
  private unsubscribtions: Array<() => void> = [];
  private maxToMonth?: string;

  constructor(
    protected localizationService: LocalizationService,
    protected repository: AccountingDocumentsInventoryRepository,
    protected notificationService: NotificationService,
    protected enumService: EnumService,
    protected eventBus: EventBus,
    protected monthOptionsService: MonthOptionsService,
    private fromMonth?: string,
    private toMonth?: string,
    private editId?: string
  ) {
    super();
    this.navigationName = editId ?? newNavigationName;
    if (editId) {
      this.createdInventoryId = editId;
      const currentYear = new Date().getFullYear();
      this.maxToMonth = `${currentYear}-12`;
    } else {
      this.maxToMonth = toMonth;
    }
  }

  @action.bound
  private syncUploadedFilesFromDetail(detail: AccountingDocumentsInventoryDetailResponse) {
    const validFiles = detail.accountFiles
      ? detail.accountFiles.map(file => ({
          canBeProcessed: true,
          inventoryAccountId: file.inventoryAccountId,
          providedFileName: file.fileName,
          resultFileName: file.fileName,
        }))
      : [];

    const validAccountIds = new Set(validFiles.map(f => f.inventoryAccountId));

    const validAccountCodesFromFileNames = new Set(
      validFiles
        .map(f => this.extractAccountCodeFromFileName(f.providedFileName ?? ""))
        .filter((code): code is string => code !== null)
    );

    const invalidFiles = this.uploadedFilesResponse.filter(f => {
      if (!f.rejectionReason || f.canBeProcessed) {
        return false;
      }

      if (f.inventoryAccountId && validAccountIds.has(f.inventoryAccountId)) {
        return false;
      }

      const accountCode = this.extractAccountCodeFromFileName(f.providedFileName ?? "");
      if (accountCode && validAccountCodesFromFileNames.has(accountCode)) {
        return false;
      }

      return true;
    });

    this.uploadedFilesResponse = [...validFiles, ...invalidFiles];
  }

  async onInitialize(): Promise<void> {
    if (this.editId) {
      const detail = await this.repository.get(this.editId);
      this.detailResponse = detail;
      this.fromMonth = detail.accountingPeriodMonthRange.from;
      this.toMonth = detail.accountingPeriodMonthRange.to;

      if (this.toMonth) {
        const [year, month] = this.toMonth.split("-");
        const targetDate = new Date(parseInt(year), parseInt(month) - 1, 1);

        const matchingOption = this.monthOptionsService.startOfMonthOptions.find(
          opt => opt.id.getTime() === targetDate.getTime()
        );

        this.selectedToMonth = matchingOption?.id ?? targetDate;
        this.initialToMonth = this.selectedToMonth;
      }

      this.syncUploadedFilesFromDetail(detail);
      this.initialUploadedFiles = [...this.uploadedFilesResponse];
    }

    if (!this.fromMonth || !this.toMonth) {
      this.requestClose();
    }

    this.unsubscribtions.push(
      reaction(
        () => this.selectedToMonth,
        () => {
          this.userSelectedToMonth = true;
        }
      )
    );

    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscribtions.forEach(unsubsribe => unsubsribe());
      this.unsubscribtions = [];
    }

    return super.onDeactivate(isClosing);
  }

  @computed
  get isEditMode() {
    return !!this.editId;
  }

  @computed
  get changeRestrictionLevel() {
    return this.detailResponse?.permittedActions?.inventoryChange?.restrictionLevel;
  }

  @computed
  get canChangeDate() {
    if (!this.createdInventoryId) {
      return true;
    }
    return this.changeRestrictionLevel === AccountingDocumentsInventoryChangeRestrictionLevel.FullChangeAllowed;
  }

  @computed
  get hasUploadedFiles() {
    return this.uploadedFilesResponse.length > 0;
  }

  @computed
  get canUpload() {
    return (this.isEditMode || this.userSelectedToMonth) && !!this.selectedToMonth;
  }

  protected postData() {
    return this.repository.create(this.item);
  }

  protected loadDetail() {
    return new CreateAccountingDocumentsInventoryRequest();
  }

  @computed
  get inventoryAccounts() {
    return this.enumService.values("inventory_accounts");
  }

  @computed
  get isPeriodComplete() {
    return !!this.selectedToMonth && !!this.fromMonth;
  }

  @computed
  get uploadedFilesCount() {
    return this.uploadedFilesResponse.filter(file => file.canBeProcessed).length;
  }

  @computed
  get totalRequiredFiles() {
    return this.inventoryAccounts.length;
  }

  @computed
  get areAllFilesUploaded() {
    return this.uploadedFilesCount === this.totalRequiredFiles;
  }

  @computed
  get inventoryAccountsWithFiles() {
    return this.inventoryAccounts.map(account => {
      const accountCode = `${account.syntheticAccount}-${account.analyticAccount}`;
      const uploadedFile = this.uploadedFilesResponse.find(file => {
        if (file.inventoryAccountId && file.inventoryAccountId === account.id) {
          return true;
        }
        if (!file.inventoryAccountId && file.providedFileName) {
          const extractedCode = this.extractAccountCodeFromFileName(file.providedFileName);
          return extractedCode === accountCode;
        }
        return false;
      });

      return {
        ...account,
        uploadedFile,
        hasFile: !!uploadedFile,
        isProcessed: uploadedFile?.canBeProcessed,
        fileName: uploadedFile?.providedFileName ?? uploadedFile?.resultFileName,
        error: uploadedFile?.rejectionReason ? this.getErrorMessageForRejectionReason(uploadedFile.rejectionReason) : undefined,
      };
    });
  }

  @bound
  extractAccountCodeFromFileName(fileName: string): string | null {
    const regex = /(\d{3})[_-](\d{2,4})/;
    const match = regex.exec(fileName);
    return match ? `${match[1]}-${match[2]}` : null;
  }

  @computed
  get fromMonthFormatted() {
    if (!this.fromMonth) {
      return "";
    }
    const [year, month] = this.fromMonth.split("-");
    return `${month}/${year}`;
  }

  @computed
  get fromMonthDate() {
    if (!this.fromMonth) {
      return undefined;
    }
    const [year, month] = this.fromMonth.split("-");
    return new Date(parseInt(year), parseInt(month) - 1, 1);
  }

  @computed
  get toMonthDate() {
    if (!this.toMonth) {
      return undefined;
    }
    const [year, month] = this.toMonth.split("-");
    return new Date(parseInt(year), parseInt(month) - 1, 1);
  }

  @computed
  get maxToMonthDate() {
    const toMonthForRange = this.maxToMonth ?? this.toMonth;
    if (!toMonthForRange) {
      return undefined;
    }
    const [year, month] = toMonthForRange.split("-");
    return new Date(parseInt(year), parseInt(month) - 1, 1);
  }

  @computed
  get availableMonths() {
    return this.monthOptionsService.startOfMonthOptions
      .filter(option => {
        if (!this.fromMonthDate || !this.maxToMonthDate) {
          return false;
        }
        const optionDate = option.id;
        return optionDate >= this.fromMonthDate && optionDate <= this.maxToMonthDate;
      })
      .map(option => ({
        ...option,
        label: option.label.replace(/^(\d{4}) - (\d{2})$/, "$2/$1"),
      }));
  }

  @bound
  async onUploadClick() {
    if (!this.canUpload) {
      return;
    }

    if (!this.createdInventoryId && this.selectedToMonth && this.fromMonth) {
      const [year, month] = [this.selectedToMonth.getFullYear(), this.selectedToMonth.getMonth() + 1];
      const toMonthStr = `${year}-${month.toString().padStart(2, "0")}`;

      this.item.accountingPeriodMonthRange = {
        from: this.fromMonth,
        to: toMonthStr,
      };

      try {
        const inventoryId = await this.repository.create(this.item);
        this.createdInventoryId = inventoryId as string;
        await this.refreshDetail();
      } catch (error) {
        return;
      }
    } else if (this.createdInventoryId && this.selectedToMonth && this.uploadedFilesCount === 0) {
      const [year, month] = [this.selectedToMonth.getFullYear(), this.selectedToMonth.getMonth() + 1];
      const toMonthStr = `${year}-${month.toString().padStart(2, "0")}`;
      const currentToMonth = this.detailResponse?.accountingPeriodMonthRange?.to;

      if (currentToMonth !== toMonthStr) {
        try {
          await this.repository.update(this.createdInventoryId, {
            accountingPeriodTo: toMonthStr,
          });
          await this.refreshDetail();
        } catch (error) {
          return;
        }
      }
    }

    this.fileInputRef?.click();
  }

  @bound
  async onFilesSelected(files: FileList | null) {
    const id = this.createdInventoryId;
    if (!files || files.length === 0 || !id) {
      return;
    }

    const fileArray = Array.from(files);
    if (fileArray.length > MAX_FILES) {
      this.notificationService.add({
        title: this.translate("fileUpload.maxFilesExceeded", MAX_FILES),
        type: NotificationType.ERROR,
      });
      if (this.fileInputRef) {
        this.fileInputRef.value = "";
      }
      return;
    }
    const invalidFiles = fileArray.filter(file => !this.validateFile(file));
    if (invalidFiles.length > 0) {
      this.notificationService.add({
        title: this.translate("fileUpload.errorProcessingFile"),
        type: NotificationType.ERROR,
      });
      if (this.fileInputRef) {
        this.fileInputRef.value = "";
      }
      return;
    }

    try {
      const response = await this.repository.uploadAccountFiles(id, fileArray);
      this.processUploadResponse(response);
      await this.refreshDetail();
    } catch (error) {
      this.notificationService.add({
        title: this.translate("fileUpload.errorProcessingFile"),
        type: NotificationType.ERROR,
      });
    } finally {
      if (this.fileInputRef) {
        this.fileInputRef.value = "";
      }
    }
  }

  @bound
  validateFile(file: File): boolean {
    const fileName = file.name.toLowerCase();
    return ALLOWED_FILE_TYPES.some(ext => fileName.endsWith(ext));
  }

  @bound
  private mergeValidFile(
    updatedFiles: AccountingDocumentsInventoryAccountFilePreviewResponse[],
    newFile: AccountingDocumentsInventoryAccountFilePreviewResponse
  ): AccountingDocumentsInventoryAccountFilePreviewResponse[] {
    const filtered = updatedFiles.filter(f => f.inventoryAccountId !== newFile.inventoryAccountId);
    filtered.push(newFile);
    return filtered;
  }

  @bound
  private mergeInvalidFile(
    updatedFiles: AccountingDocumentsInventoryAccountFilePreviewResponse[],
    newFile: AccountingDocumentsInventoryAccountFilePreviewResponse
  ): AccountingDocumentsInventoryAccountFilePreviewResponse[] {
    const existingIndex = newFile.providedFileName
      ? updatedFiles.findIndex(f => f.providedFileName === newFile.providedFileName)
      : -1;

    if (existingIndex >= 0) {
      updatedFiles[existingIndex] = newFile;
    } else {
      updatedFiles.push(newFile);
    }
    return updatedFiles;
  }

  @bound
  private showErrorNotificationForFile(fileResponse: AccountingDocumentsInventoryAccountFilePreviewResponse) {
    if (!fileResponse.canBeProcessed && fileResponse.rejectionReason) {
      const errorMessage = this.getErrorMessageForRejectionReason(fileResponse.rejectionReason);
      this.notificationService.add({
        title: `Soubor #${fileResponse.providedFileName} ${errorMessage}`,
        type: NotificationType.ERROR,
      });
    }
  }

  @bound
  processUploadResponse(response: AccountingDocumentsInventoryAccountFilesResponse) {
    if (!response.items) {
      return;
    }

    let updatedFiles = [...this.uploadedFilesResponse];

    for (const newFile of response.items) {
      if (newFile.canBeProcessed && newFile.inventoryAccountId) {
        updatedFiles = this.mergeValidFile(updatedFiles, newFile);
      } else {
        updatedFiles = this.mergeInvalidFile(updatedFiles, newFile);
      }
    }

    this.uploadedFilesResponse = updatedFiles;
    response.items.forEach(fileResponse => {
      this.showErrorNotificationForFile(fileResponse);
    });
  }

  @bound
  getErrorMessageForRejectionReason(reason: AccountingDocumentsInventoryFileRejectionReason): string {
    let errorKey: string;
    switch (reason) {
      case AccountingDocumentsInventoryFileRejectionReason.INVALID_ROW_VALUES:
        errorKey = "fileUpload.rejectionReasons.invalidRowValues";
        break;
      case AccountingDocumentsInventoryFileRejectionReason.DUPLICATE_DOCUMENT_FILE_EXISTS:
        errorKey = "fileUpload.rejectionReasons.duplicateFile";
        break;
      case AccountingDocumentsInventoryFileRejectionReason.INCORRECT_DOCUMENT_FILE_FORMAT_FOUND:
        errorKey = "fileUpload.rejectionReasons.incorrectFormat";
        break;
      case AccountingDocumentsInventoryFileRejectionReason.INCORRECT_DOCUMENT_FILE_NAME_FOUND:
        errorKey = "fileUpload.rejectionReasons.incorrectFileName";
        break;
      case AccountingDocumentsInventoryFileRejectionReason.REQUIRED_COLUMNS_NOT_FOUND:
        errorKey = "fileUpload.rejectionReasons.requiredColumnsNotFound";
        break;
      case AccountingDocumentsInventoryFileRejectionReason.INVALID_ACCOUNTING_PERIOD_FOUND:
        errorKey = "fileUpload.rejectionReasons.invalidAccountingPeriod";
        break;
      case AccountingDocumentsInventoryFileRejectionReason.INVENTORY_ACCOUNT_NOT_FOUND:
        errorKey = "fileUpload.rejectionReasons.inventoryAccountNotFound";
        break;
      case AccountingDocumentsInventoryFileRejectionReason.DOCUMENT_FILE_FOR_INVENTORY_ACCOUNT_EXISTS:
        errorKey = "fileUpload.rejectionReasons.fileForAccountExists";
        break;
      case AccountingDocumentsInventoryFileRejectionReason.ROWS_WITH_OTHER_INVENTORY_ACCOUNT_FOUND:
        errorKey = "fileUpload.rejectionReasons.rowsWithOtherAccount";
        break;
      default:
        errorKey = "fileUpload.rejectionReasons.unknown";
    }

    return this.translate(errorKey);
  }

  @action.bound
  setFileInputRef(ref: HTMLInputElement | null) {
    if (ref) {
      this.fileInputRef = ref;
    }
  }

  @bound
  async onDownloadFile(inventoryAccountId?: string) {
    if (!this.createdInventoryId || !inventoryAccountId) {
      return;
    }

    try {
      const response = await this.repository.getAccountFile(this.createdInventoryId, inventoryAccountId);
      const uploadedFile = this.uploadedFilesResponse.find(file => file.inventoryAccountId === inventoryAccountId);
      const defaultFileName = uploadedFile?.providedFileName ?? uploadedFile?.resultFileName ?? "file.xlsx";
      download(response.data, parseFileNameFromAxiosResponse(response, defaultFileName));
    } catch (error) {
      this.notificationService.add({
        title: this.translate("fileDownload.error"),
        type: NotificationType.ERROR,
      });
    }
  }

  @bound
  async onDeleteFile(inventoryAccountId?: string) {
    if (!this.createdInventoryId || !inventoryAccountId) {
      return;
    }

    try {
      await this.repository.deleteAccountFile(this.createdInventoryId, inventoryAccountId);
      this.uploadedFilesResponse = this.uploadedFilesResponse.filter(file => file.inventoryAccountId !== inventoryAccountId);
      await this.refreshDetail();
    } catch (error) {
      this.notificationService.add({
        title: this.translate("fileDelete.error"),
        type: NotificationType.ERROR,
      });
    }
  }

  @bound
  onDeleteErrorFile(fileName: string) {
    this.uploadedFilesResponse = this.uploadedFilesResponse.filter(file => file.providedFileName !== fileName);
  }

  @action.bound
  async refreshDetail() {
    if (!this.createdInventoryId) {
      return;
    }

    try {
      const detail = await this.repository.get(this.createdInventoryId);
      this.detailResponse = detail;
      this.syncUploadedFilesFromDetail(detail);
    } catch (error) {
      console.error("Failed to refresh detail:", error);
    }
  }

  @action.bound
  async onSave() {
    if (!this.editId || !this.selectedToMonth) {
      return;
    }

    try {
      const [year, month] = [this.selectedToMonth.getFullYear(), this.selectedToMonth.getMonth() + 1];
      const toMonthStr = `${year}-${month.toString().padStart(2, "0")}`;

      await this.repository.update(this.editId, {
        accountingPeriodTo: toMonthStr,
      });

      this.eventBus.publish({ type: `${this.modelName}.updated`, payload: {} });
    } catch (error) {}
  }

  @action.bound
  async onStartInventoryCheck() {
    if (!this.createdInventoryId) {
      return;
    }

    try {
      await this.repository.checks(this.createdInventoryId);
      this.eventBus.publish({ type: `${this.modelName}.updated`, payload: {} });
      this.requestClose();
    } catch (error) {
      this.notificationService.add({
        title: this.translate("check.error"),
        type: NotificationType.ERROR,
      });
    }
  }

  @computed
  get canStartCheck() {
    return (
      this.isPeriodComplete &&
      this.areAllFilesUploaded &&
      !!this.createdInventoryId &&
      this.detailResponse?.permittedActions.inventoryCheck.isAllowed
    );
  }

  @computed
  get canSave() {
    if (!this.isEditMode) {
      return false;
    }
    return this.canChangeDate;
  }

  @action.bound
  onClose() {
    this.eventBus.publish({ type: `${this.modelName}.updated`, payload: {} });
    this.requestClose();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.documentsInventory.create.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (fromMonth?: string, toMonth?: string, editId?: string) =>
      new DocumentsInventoryCreateViewModel(
        container.get(LocalizationService),
        container.get(AccountingDocumentsInventoryRepository),
        container.get(NotificationService),
        container.get(EnumService),
        container.get(EventBus),
        container.get(MonthOptionsService),
        fromMonth,
        toMonth,
        editId
      );
  }
}
